/* eslint-disable react-refresh/only-export-components -- co-locating Provider + hook for the references context is intentional and matches the plan spec */
import { createContext, useContext, type ReactNode } from 'react';

export type RefMeta = {
  /** Resolved display name from positional name map, e.g. "1.2". */
  name: string;
  /** Full hierarchical hover title (book prefix when cross-book or external). */
  hoverTitle: string;
  /** Target's book id. Null indicates intra-doc (same as current book). */
  targetBookId: string | null;
  /** Target's node id. Null indicates intra-doc (same as current node). */
  targetNodeId: string | null;
  /** Whether this is an internal block ref or an external URL ref. */
  kind: 'internal' | 'url';
  /** Review status of the reference. */
  status: 'valid' | 'ignored';
  /** Target block missing — unresolved internal ref (not ignored). */
  lost?: boolean;
  /** For URL-kind refs, the destination URL. Null for internal refs. */
  externalUrl: string | null;
  /**
   * Partial-paragraph anchor: text snippet inside the target block that the
   * reference points at. Null targets the whole block.
   */
  targetFragment: string | null;
  /** Reference row id (database). */
  rowId?: string;
  /** Target block id for internal refs. */
  refId?: string | null;
  /** User template / frozen label. */
  customName?: string | null;
  /** Fallback phrase used when target is lost. */
  detectedPhrase?: string | null;
};

const ReferencesContext = createContext<Map<string, RefMeta>>(new Map());

export function ReferencesProvider({
  value,
  children,
}: {
  value: Map<string, RefMeta>;
  children: ReactNode;
}) {
  return (
    <ReferencesContext.Provider value={value}>
      {children}
    </ReferencesContext.Provider>
  );
}

export function useReferenceMap(): Map<string, RefMeta> {
  return useContext(ReferencesContext);
}
